import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  notificationsService,
  NotificationRule,
  NotificationSeverity,
  NotificationTriggerEvent,
  CreateNotificationRuleDto,
  EventCatalog,
} from '../services/notificationsService';

const emptyForm: CreateNotificationRuleDto = {
  name: '',
  description: '',
  trigger_event: 'user.created',
  severity: 'info',
  message_template: '',
  enabled: true,
  condition: {},
};

const NotificationRulesPage: React.FC = () => {
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [catalog, setCatalog] = useState<EventCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateNotificationRuleDto>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [conditionMode, setConditionMode] = useState<'always' | 'field'>('always');
  const [conditionField, setConditionField] = useState('');
  const [conditionOp, setConditionOp] = useState<'eq' | 'neq' | 'contains' | 'gt' | 'lt'>('eq');
  const [conditionValue, setConditionValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, c] = await Promise.all([
        notificationsService.listRules(),
        notificationsService.getEventCatalog(),
      ]);
      setRules(r);
      setCatalog(c);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // When severity changes & color is empty (new rule), auto-fill from catalog
  useEffect(() => {
    if (!editingId && catalog && (!form.color || form.color === '')) {
      setForm((f) => ({ ...f, color: catalog.severity_colors[f.severity] }));
    }
  }, [form.severity, editingId, catalog, form.color]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      color: catalog?.severity_colors.info ?? '#3b82f6',
    });
    setConditionMode('always');
    setConditionField('');
    setConditionValue('');
    setShowForm(true);
  };

  const openEdit = (rule: NotificationRule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      description: rule.description ?? '',
      trigger_event: rule.trigger_event,
      severity: rule.severity,
      color: rule.color,
      message_template: rule.message_template,
      enabled: rule.enabled,
      condition: rule.condition,
    });
    if (rule.condition && rule.condition.field) {
      setConditionMode('field');
      setConditionField(rule.condition.field);
      setConditionOp(rule.condition.op ?? 'eq');
      setConditionValue(String(rule.condition.value ?? ''));
    } else {
      setConditionMode('always');
      setConditionField('');
      setConditionValue('');
    }
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  const save = async () => {
    setError(null);
    try {
      const condition =
        conditionMode === 'field' && conditionField
          ? { field: conditionField, op: conditionOp, value: parseValue(conditionValue) }
          : {};
      const payload = { ...form, condition };
      if (editingId) {
        await notificationsService.updateRule(editingId, payload);
      } else {
        await notificationsService.createRule(payload);
      }
      setShowForm(false);
      setEditingId(null);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save rule');
    }
  };

  const toggleEnabled = async (rule: NotificationRule) => {
    await notificationsService.updateRule(rule.id, { enabled: !rule.enabled });
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));
  };

  const remove = async (id: number) => {
    if (!confirm('Delete this rule?')) return;
    await notificationsService.deleteRule(id);
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Notification Rules</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            Define which events produce in-app notifications and how they look.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/notifications"
            className="text-sm text-[var(--color-text-link)] hover:underline"
          >
            View notifications
          </Link>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-md text-sm font-medium bg-[var(--color-primary)] text-[var(--color-text-inverse)] hover:opacity-90"
            data-testid="new-rule-button"
          >
            + New rule
          </button>
        </div>
      </div>

      {error && !showForm && (
        <div
          className="p-3 mb-4 text-sm rounded-md"
          style={{
            backgroundColor: 'var(--color-status-error-light, #fef2f2)',
            color: 'var(--color-error)',
          }}
        >
          {error}
        </div>
      )}

      {showForm && (
        <div
          className="mb-6 p-5 rounded-lg border"
          style={{
            backgroundColor: 'var(--color-background-card)',
            borderColor: 'var(--color-border-default)',
          }}
        >
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-text-primary)]">
            {editingId ? 'Edit rule' : 'New rule'}
          </h2>

          {error && (
            <div
              className="p-2 mb-3 text-sm rounded"
              style={{
                backgroundColor: 'var(--color-status-error-light, #fef2f2)',
                color: 'var(--color-error)',
              }}
            >
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <input
                type="text"
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="rule-name-input"
              />
            </Field>

            <Field label="Trigger event">
              <select
                className={inputCls}
                value={form.trigger_event}
                onChange={(e) =>
                  setForm({
                    ...form,
                    trigger_event: e.target.value as NotificationTriggerEvent,
                  })
                }
                data-testid="rule-event-select"
              >
                {(catalog?.events ?? []).map((ev) => (
                  <option key={ev} value={ev}>
                    {ev}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Severity">
              <select
                className={inputCls}
                value={form.severity}
                onChange={(e) => {
                  const sev = e.target.value as NotificationSeverity;
                  setForm({
                    ...form,
                    severity: sev,
                    color: catalog?.severity_colors[sev] ?? form.color,
                  });
                }}
                data-testid="rule-severity-select"
              >
                {(catalog?.severities ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Color (hex)">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color || '#3b82f6'}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-10 h-9 cursor-pointer border rounded"
                  style={{ borderColor: 'var(--color-border-default)' }}
                  data-testid="rule-color-picker"
                />
                <input
                  type="text"
                  className={inputCls}
                  value={form.color ?? ''}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="#3b82f6"
                  data-testid="rule-color-hex"
                />
              </div>
            </Field>

            <Field label="Description (optional)" full>
              <input
                type="text"
                className={inputCls}
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>

            <Field label="Message template" full>
              <input
                type="text"
                className={inputCls}
                value={form.message_template}
                onChange={(e) => setForm({ ...form, message_template: e.target.value })}
                placeholder="A new {{role}} {{email}} was created"
                data-testid="rule-message-template"
              />
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                Supports <code>{'{{path.to.value}}'}</code> placeholders from the event payload.
              </p>
            </Field>

            <Field label="Condition" full>
              <div className="flex items-center gap-3 mb-2">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <input
                    type="radio"
                    checked={conditionMode === 'always'}
                    onChange={() => setConditionMode('always')}
                    name="cond-mode"
                  />
                  Always fire
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <input
                    type="radio"
                    checked={conditionMode === 'field'}
                    onChange={() => setConditionMode('field')}
                    name="cond-mode"
                  />
                  Only when field matches
                </label>
              </div>
              {conditionMode === 'field' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="field (e.g., role)"
                    value={conditionField}
                    onChange={(e) => setConditionField(e.target.value)}
                    data-testid="rule-cond-field"
                  />
                  <select
                    className={inputCls}
                    value={conditionOp}
                    onChange={(e) =>
                      setConditionOp(e.target.value as 'eq' | 'neq' | 'contains' | 'gt' | 'lt')
                    }
                    data-testid="rule-cond-op"
                  >
                    <option value="eq">equals</option>
                    <option value="neq">not equals</option>
                    <option value="contains">contains</option>
                    <option value="gt">greater than</option>
                    <option value="lt">less than</option>
                  </select>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="value"
                    value={conditionValue}
                    onChange={(e) => setConditionValue(e.target.value)}
                    data-testid="rule-cond-value"
                  />
                </div>
              )}
            </Field>

            <Field label="Enabled" full>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.enabled ?? true}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="rule-enabled-checkbox"
                />
                Active
              </label>
            </Field>
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={cancelForm}
              className="px-4 py-2 text-sm rounded-md border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              className="px-4 py-2 text-sm font-medium rounded-md bg-[var(--color-primary)] text-[var(--color-text-inverse)] hover:opacity-90"
              data-testid="rule-save-button"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-sm text-[var(--color-text-secondary)]">Loading…</div>
      ) : rules.length === 0 ? (
        <div
          className="p-8 rounded-lg border text-center text-[var(--color-text-secondary)]"
          style={{
            borderColor: 'var(--color-border-default)',
            backgroundColor: 'var(--color-background-card)',
          }}
        >
          No rules yet. Create one to start receiving notifications.
        </div>
      ) : (
        <ul
          className="divide-y rounded-lg border overflow-hidden"
          style={{
            borderColor: 'var(--color-border-default)',
            backgroundColor: 'var(--color-background-card)',
          }}
        >
          {rules.map((rule) => (
            <li key={rule.id} className="p-4" data-testid={`rule-row-${rule.id}`}>
              <div className="flex items-start gap-3">
                <span
                  className="mt-1.5 inline-block w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: rule.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {rule.name}
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                      style={{ backgroundColor: rule.color + '22', color: rule.color }}
                    >
                      {rule.severity}
                    </span>
                    {!rule.enabled && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-[var(--color-background-surface)] text-[var(--color-text-muted)]">
                        disabled
                      </span>
                    )}
                  </div>
                  {rule.description && (
                    <div className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                      {rule.description}
                    </div>
                  )}
                  <div className="text-xs text-[var(--color-text-muted)] mt-1">
                    {rule.trigger_event} → "{rule.message_template}"
                    {rule.condition?.field && (
                      <>
                        {' '}
                        when <code>{rule.condition.field}</code> {rule.condition.op}{' '}
                        <code>{String(rule.condition.value)}</code>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleEnabled(rule)}
                    className="text-xs px-2 py-1 rounded border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)]"
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(rule)}
                    className="text-xs px-2 py-1 rounded border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)]"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(rule.id)}
                    className="text-xs px-2 py-1 rounded text-[var(--color-error)] hover:bg-[var(--color-status-error-light)]"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const inputCls =
  'w-full px-3 py-2 text-sm rounded-md bg-[var(--color-background-input)] text-[var(--color-text-primary)] border border-[var(--color-border-default)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]';

const Field: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({
  label,
  full,
  children,
}) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-1">
      {label}
    </label>
    {children}
  </div>
);

function parseValue(s: string): unknown {
  if (s === 'true') return true;
  if (s === 'false') return false;
  const num = Number(s);
  if (s !== '' && !Number.isNaN(num)) return num;
  return s;
}

export default NotificationRulesPage;
